import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import { useV2Shell } from './useV2Shell';
import { frameMainClass, V2_PAGE_MAIN_CLASS } from './v2Shell';

/**
 * The signed-in frame: sidebar, top bar, the scrolling main area, and the
 * advisor slot. ProtectedRoute renders it around every page; DevFrame renders
 * the same frame around DEV previews.
 *
 * v1 users: today's frame, class for class. v2 shell: the mock's 72px rail
 * (drawer at 860px and below, behind the mock's navy scrim), and on the v2
 * page routes the main area has no padding and the mock's page ground, so the
 * page's own `.page` geometry governs (`frameMainClass`).
 */
export function AppFrame({ children, bare }: {
  children: React.ReactNode;
  /** DevFrame only: force the v2 page frame (no padding) off a v2 route. */
  bare?: boolean;
}): React.ReactElement {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const mainContentRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  const v2 = useV2Shell();

  // v2 drawer: a link click closes it, as the mock's page navigation does.
  useEffect(() => {
    if (v2) setSidebarOpen(false);
  }, [pathname, v2]);

  return (
    // `ds-touch`: the v2 frame's phone touch targets (ds.css "Touch targets").
    <div className={`flex h-screen bg-gray-100${v2 ? ' ds-touch' : ''}`}>
      <Sidebar isOpen={sidebarOpen} />

      {sidebarOpen && (
        <div
          className={v2 ? 'fixed inset-0 z-30 bg-[rgba(3,26,66,.35)] min-[861px]:hidden' : 'fixed inset-0 z-[5] bg-black/40 lg:hidden'}
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <div className={`flex flex-col flex-1 overflow-hidden ${v2 ? 'min-[861px]:pl-[72px]' : 'lg:pl-20'}`}>
        <Navbar toggleSidebar={() => setSidebarOpen((open) => !open)} />
        {/* Positioned so absolutely-positioned descendants (sr-only captions) resolve against this scroller, not the document */}
        {/* id: the area an expanded advisor panel covers (advisorPanelMode.ts). */}
        <main id="frame-main" ref={mainContentRef} className={v2 && bare ? V2_PAGE_MAIN_CLASS : frameMainClass(v2, pathname)}>
          {children}
        </main>
      </div>

      {
        /* Frame-level advisor slot (Mixpanel pattern, Ari 2026-08-12).
        display:contents — a portaled aside participates in this flex
        row directly: full viewport height, beside the compressed
        column. Empty when no page portals into it. */
      }
      <div id="advisor-slot" className="contents" />
    </div>
  );
}

export default AppFrame;
